/**
 * @file
 *
 * Configures Select2 fields backed by the Elon directory.
 */

/**
 * Get employees from a directory response, including empty responses.
 *
 * @param {Object} data Directory API response.
 * @return {Array|Object} Directory employees or an empty array.
 */
function getDirectoryEmployees(data) {
	if (!data || !data.ArrayOfEmployee || !data.ArrayOfEmployee.Employee) {
		return [];
	}

	return data.ArrayOfEmployee.Employee;
}

/**
 * Normalize an employee returned by the directory.
 *
 * @param {Object} employee Directory employee.
 * @return {Object} Values used by Select2.
 */
function normalizeEmployee(employee) {
	const name = `${employee.FirstName} ${employee.LastName}`;

	return {
		id: `${employee.Username},${name}`,
		name,
		email: employee.EmailAddress,
		position: employee.Position,
		department: employee.DepartmentName,
		username: employee.Username,
	};
}

/**
 * Build a PhotoManager URL for a directory user.
 *
 * @param {string} username Directory username.
 * @param {boolean} [square=true] Whether to use a square crop instead of a portrait crop.
 * @param {number} [width=120] Requested image width in pixels.
 * @return {string} Directory image URL.
 */
function getDirectoryImageUrl(username, square = true, width = 120) {
	const crop = square ? '1:1' : '5:7';

	return `/PhotoManager/GetImage.ashx?user=${username}&crop=${crop}&w=${width}`;
}

/**
 * Create the shared directory-backed Select2 configuration.
 *
 * @param {Function} $ jQuery with Select2 attached.
 * @param {Function} renderResult Render a directory search result.
 * @return {Object} Select2 configuration.
 */
function createDirectoryOptions($, renderResult) {
	return {
		allowClear: true,
		multiple: true,
		minimumInputLength: 1,
		ajax: {
			url: function() {
				return '/Directories/xml.ashx';
			},
			data: function(params) {
				return {
					full: false,
					type: 'json',
					q: params.term,
				};
			},
			dataType: 'json',
			type: 'GET',
			processResults: function(data) {
				return {
					results: $.map(getDirectoryEmployees(data), function(employee) {
						const person = normalizeEmployee(employee);

						return {
							id: person.id,
							text: person.name,
							html: renderResult(person),
							title: person.name,
						};
					}),
				};
			},
		},
		escapeMarkup: function(markup) {
			return markup;
		},
		templateResult: function(data) {
			return data.html;
		},
		templateSelection: function(data) {
			return data.text;
		},
	};
}

/**
 * Render a directory result for admin fields.
 *
 * @param {Object} person Normalized directory employee.
 * @return {string} Select2 result markup.
 */
function renderAdminResult(person) {
	const photo = getDirectoryImageUrl(person.username, false, 64);

	return '<div class="directory-item"><img alt="" src="' + photo + '" style="float: left; margin-right: 6px;" /> <strong>' + person.name + '</strong><br />' + person.email + '<br />' + person.position + '<br />' + person.department + '<div style="clear: both; height: 1px;"></div></div>';
}

/**
 * Render a directory result for frontend fields.
 *
 * @param {Object} person Normalized directory employee.
 * @return {string} Select2 result markup.
 */
function renderFrontendResult(person) {
	const photo = getDirectoryImageUrl(person.username);

	return '<div class="directory-item"><div class="directory-item--img"><img alt="" src="' + photo + '" /></div><div class="directory-item--text"><span class="directory-name"><strong>' + person.name + '</strong></span><span class="directory-title">' + person.position + '</span><span class="directory-email">' + person.email + '</span></div>';
}

/**
 * Initialize directory-backed Select2 fields in wp-admin.
 *
 * @param {Function} $ jQuery with Select2 attached.
 */
export function initializeAdminDirectoryUserSelects($) {
	const $fields = $('#department_admins, #acf-field_5d412bb33f596');

	if (!$fields.length) {
		return;
	}

	// For "People In Article", we're only loading options that are already stored in the DB,
	// so go ahead and set them to "selected".
	$('#acf-field_5d412bb33f596 option').prop('selected', true);

	// Manually setting an inline style of width 100% at rbass4's request.
	$('#acf-field_5d412bb33f596').css('width', '100%');

	$fields.select2(Object.assign(
		{
			placeholder: 'Search for individual by name or username',
		},
		createDirectoryOptions($, renderAdminResult)
	));
}

/**
 * Initialize directory-backed Select2 fields on the frontend.
 *
 * @param {Function} $ jQuery with Select2 attached.
 */
export function initializeFrontendDirectoryUserSelects($) {
	const $fields = $('#search-author, #tag-user');

	if (!$fields.length) {
		return;
	}

	$fields.select2(createDirectoryOptions($, renderFrontendResult));
}
